/**
 * NOTE (Ryan, 2026-02-03): useNangoConnect hook
 *
 * Provides functionality to initiate OAuth connection flows via Nango Connect UI.
 * Uses a TanStack Query mutation so the caller gets loading state (isPending) for free.
 * Resolves with connectionId on success, undefined if user closed the modal, or throws on error.
 */

import { useCallback, useRef } from "react";

import Nango from "@nangohq/frontend";
import { useMutation } from "@tanstack/react-query";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { ReploError } from "schemas/errors";

class SaveConnectionError extends ReploError {}

interface UseNangoConnectOptions {
  projectId: string;
}

interface ConnectVariables {
  integrationKey: string;
}

/**
 * Runs the Nango connect flow. Resolves with connectionId on success,
 * undefined if the user closed the modal without connecting, or rejects with Error.
 */
export function useNangoConnect({ projectId }: UseNangoConnectOptions) {
  const connectUIRef = useRef<ReturnType<Nango["openConnectUI"]> | null>(null);

  const { mutateAsync: createConnectSession } =
    agentIntegrationsRouter.createConnectSession.useMutation();
  const { mutateAsync: saveConnection } =
    agentIntegrationsRouter.saveConnection.useMutation();

  const connectMutation = useMutation({
    mutationFn: async ({
      integrationKey,
    }: ConnectVariables): Promise<string | undefined> => {
      const { token } = await createConnectSession({
        integrationKey,
        projectId,
      });

      return new Promise<string | undefined>((resolve, reject) => {
        const nango = new Nango({ connectSessionToken: token });

        const connectUI = nango.openConnectUI({
          // Note (Noah, 2026-03-05): This is actually an incorrect lint rule - onEvent accepts promises
          // oxlint-disable-next-line typescript/no-floating-promises
          onEvent: async (event) => {
            await exhaustiveSwitch(event)({
              connect: async (ev) => {
                try {
                  const nangoConnectionId = ev.payload.connectionId;
                  const { connectionId } = await saveConnection({
                    integrationKey,
                    nangoConnectionId,
                    projectId,
                  });
                  connectUI.close();
                  resolve(connectionId);
                } catch (saveError) {
                  connectUI.close();
                  reject(
                    new SaveConnectionError({
                      message:
                        saveError instanceof Error
                          ? saveError.message
                          : "Failed to save connection",
                      cause: saveError,
                    }),
                  );
                }
              },
              error: async (ev) => {
                connectUI.close();
                reject(new Error(ev.payload.errorMessage));
              },
              close: async () => {
                resolve(undefined);
              },
              ready: async () => {},
              settings_changed: async () => {},
            });
          },
        });

        connectUIRef.current = connectUI;
      });
    },
  });

  const close = useCallback(() => {
    connectUIRef.current?.close();
    connectUIRef.current = null;
  }, []);

  return {
    connectMutation,
    close,
  };
}
